/**
 * GameLaunchCoordinator
 *
 * Manages Playjector (game launcher) lifecycle and state.
 *
 * Responsibilities:
 * - Track game launch/close lifecycle
 * - Monitor Steam login state
 * - Handle game launch timeouts and error scenarios
 * - Manage campaign launch flow (game launching only)
 * - Send game lifecycle analytics
 *
 * ARCHITECTURE:
 * - Owns game/playjector state (gameLaunched, gameId, gameName, etc.)
 * - Does NOT own matchmaking state (campaignId, matchmakeable, etc.)
 * - Communicates with PavilionCoordinator via EVENTS ONLY
 * - PavilionCoordinator handles all API communication and matchmaking state
 *
 * Event Flow:
 * - PavilionCoordinator.campaignAssigned → GameLaunchCoordinator launches game
 * - Game launches → GameLaunchCoordinator.campaignLaunchSucceeded → PavilionCoordinator updates state + API
 * - Game fails → GameLaunchCoordinator.campaignLaunchFailed → PavilionCoordinator updates state + API
 *
 * Status: Campaign launch flow implemented with timeout tracking
 */

import { makeStateStore } from '@playcastdotio/StateStore';
import type { StateStore, PlayjectorHostEdition } from '@playcastdotio/CoreTypes';
import { GameLifecycleManager } from './managers/GameLifecycleManager';
import { CampaignSwitchManager } from './managers/CampaignSwitchManager';
import { MacroManager } from './managers/MacroManager';
import { GameProcessStopManager } from './managers/GameProcessStopManager';
import type { LaunchContinuationOwner } from '../_types/LaunchContinuation';
import type { IPlayjectorService, PlayjectorState, CampaignLaunchResult } from '../../services/playjector/interfaces/IPlayjectorService';
import type { AppCoordinator } from '../app/AppCoordinator';
import { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type { IReplicationRouter } from '../ReplicationRouter';
import { CoordinatorEvent, CoordinatorEventImpl } from '../_types/events';
import type { IHostAnalyticsManager } from '../../services/analytics/IHostAnalyticsManager';
import type { HostCampaignConfig } from '@playcastdotio/Messaging';
import type { GameSaveCampaign, IGameSaveManager } from '../../managers/game-save/IGameSaveManager';
import type {
  GameSaveConfigurationAvailabilityResult,
  IGameSaveConfigurationAvailability,
} from '../../services/gameSave/IGameSaveConfigurationAvailability';
import * as Sentry from '@sentry/react';
import type { ICampaignGameSaveConfigurationStore } from '../../managers/game-save/GameSaveManager';


// ========================================
// Types
// ========================================

export type GameLaunchCoordinatorState = {
  initialized: boolean;
};

// Shared state types for managers
export type GameLaunchModeState = {
  gameLaunched: boolean;
  gameName: string;
  gameId: number;
  steamLaunched: boolean;
  steamWindow: boolean;
  userLoggedIn: boolean;
};

export type GameLaunchMatchmakerState = {
  stateDetails?: {
    currentMatchInvitationId?: string;
  };
};

// NOTE: Matchmaking state is owned by PavilionCoordinator, not this coordinator

export type AdModeState = {
  enabled: boolean;
};

type CampaignSavePreparation =
  | { status: 'ready'; configured: boolean }
  | { status: 'failed'; reason: string }
  | { status: 'superseded' };

// Event types imported from shared location

// ========================================
// GameLaunchCoordinator (Skeleton)
// ========================================

export class GameLaunchCoordinator {
  private state: StateStore<GameLaunchCoordinatorState>;

  // Per-launch idempotency flag for `signalLaunchMacroWalkComplete()`.
  // Reset on every fresh `gameLaunched` event so each launch can emit `gameReady`
  // exactly once. Defaults to `true` until the first launch starts so we never
  // emit `gameReady` for a never-launched coordinator.
  private launchMacroWalkComplete = true;

  // Snapshot of the most recent gameLaunched event payload. Used by both
  // `signalLaunchMacroWalkComplete()` (stateful walk completion) and
  // `executeLegacyOnLaunchMacro()` (legacy fallback) so the gameReady event
  // carries identical (gameId, gameName) regardless of which path completed.
  private lastLaunchData: { gameId: number; gameName: string } | null = null;
  private launchMacroGeneration = 0;
  private admittedLaunchGeneration = 0;
  /** A checked save must never silently become a legacy launch in this host session. */
  private readonly confirmedConfiguredSaveKeys = new Set<string>();

  // Stores (GameLaunchCoordinator owns game/playjector state ONLY)
  private gameModeStore: StateStore<GameLaunchModeState>;
  private matchmakerStore: StateStore<GameLaunchMatchmakerState>;
  private adModeStore: StateStore<AdModeState>;

  // NOTE: Matchmaking state (campaignId, matchmakeable, etc.) is owned by PavilionCoordinator!
  // We communicate via events, not by modifying each other's state.

  private managers: {
    lifecycle: GameLifecycleManager;
    campaignSwitch: CampaignSwitchManager;
    macro: MacroManager;
    processStop: GameProcessStopManager;
  };

  // Service and external coordinator references
  private playjectorService: IPlayjectorService;
  private pavilionCoordinator?: PavilionCoordinator; // Needed for MacroManager to fetch campaign config
  private unsubscribeFromService?: () => void;
  private campaignLaunchGeneration = 0;

  public events = {
    launchWorkInvalidated: new CoordinatorEventImpl<{
      reason: string; nextOwner: LaunchContinuationOwner;
    }>(),
    gameLaunched: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
    gameClosed: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
    steamUserLoggedOut: new CoordinatorEventImpl<void>(),
    logoutTimeoutExpired: new CoordinatorEventImpl<void>(),
    gameInfoUpdated: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
    // Campaign switch events (for cross-coordinator communication with PavilionCoordinator)
    campaignSwitchSucceeded: new CoordinatorEventImpl<{ campaignId: string }>(),
    campaignSwitchFailed: new CoordinatorEventImpl<{ campaignId: string; reason: string; transient?: boolean }>(),
    gameSaveRestoreStarted: new CoordinatorEventImpl<{ campaignId: string }>(),
    gameSaveCampaignConfigurationSelected: new CoordinatorEventImpl<{
      campaignId: string; generation: number; configuration: HostCampaignConfig['gameSave'];
    }>(),
    gameSaveRestored: new CoordinatorEventImpl<{ campaignId: string }>(),
    gameSaveConfigurationChecked: new CoordinatorEventImpl<{ campaignId: string; configured: boolean }>(),
    gameSaveLaunchDispatched: new CoordinatorEventImpl<{ campaignId: string; generation: number }>(),
    gameSaveLaunchConfirmed: new CoordinatorEventImpl<{ campaignId: string; generation: number }>(),
    // Fires after gameLaunched AND after onLaunchMacro completes (used by PavilionCoordinator to set matchmakeable)
    gameReady: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
  };

  constructor(
    playjectorService: IPlayjectorService,
    private readonly appCoordinator: AppCoordinator,
    private readonly analytics: IHostAnalyticsManager,
    private readonly gameSaveManager?: IGameSaveManager,
    private readonly gameSaveConfigurationAvailability?: IGameSaveConfigurationAvailability,
    private readonly campaignGameSaveConfiguration?: ICampaignGameSaveConfigurationStore,
  ) {
    console.log('[GameLaunchCoordinator] Constructing');

    // Service must be injected (for testing and production)
    this.playjectorService = playjectorService;

    this.state = makeStateStore<GameLaunchCoordinatorState>({
      initialized: false,
    });

    // Initialize stores
    this.gameModeStore = makeStateStore<GameLaunchModeState>({
      gameLaunched: false,
      gameName: '',
      gameId: 0,
      steamLaunched: false,
      steamWindow: false,
      userLoggedIn: false,
    });

    this.matchmakerStore = makeStateStore<GameLaunchMatchmakerState>({
      stateDetails: {},
    });

    // Matchmaking state is owned by PavilionCoordinator, not us!

    this.adModeStore = makeStateStore<AdModeState>({
      enabled: false,
    });

    // NOTE: MacroManager needs PavilionCoordinator reference, but we don't have it yet
    // MacroManager will be initialized in init() method after pavilionCoordinator is passed
    this.managers = {
      processStop: new GameProcessStopManager(playjectorService),
      lifecycle: new GameLifecycleManager(
        {
          gameMode: this.gameModeStore,
          matchmaker: this.matchmakerStore,
          adMode: this.adModeStore,
        },
        this.events,
        this.appCoordinator,
        this.analytics,
      ),
      campaignSwitch: new CampaignSwitchManager(
        {
          gameMode: this.gameModeStore,
          matchmaker: this.matchmakerStore,
          adMode: this.adModeStore,
        },
      ),
      // MacroManager initialized in init()
      macro: null as unknown as MacroManager, // Temporary placeholder
    };
  }

  /**
   * Initialize the coordinator
   */
  public async init(pavilionCoordinator?: PavilionCoordinator, sessionCoordinator?: SessionCoordinator): Promise<void> {
    console.log('[GameLaunchCoordinator] Initializing');

    // Store pavilionCoordinator reference (needed for MacroManager)
    this.pavilionCoordinator = pavilionCoordinator;

    // Initialize MacroManager (requires pavilionCoordinator)
    if (pavilionCoordinator) {
      this.managers.macro = new MacroManager(pavilionCoordinator, this.analytics);
      console.log('[GameLaunchCoordinator] ✅ MacroManager initialized');
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager skipped (no PavilionCoordinator)');
      Sentry.logger.warn('[GameLaunchCoordinator] ⚠️  MacroManager skipped (no PavilionCoordinator)')
    }

    // Subscribe to Playjector state changes
    this.unsubscribeFromService = this.playjectorService.onStateChange((playjectorState) => {
      this.handlePlayjectorStateChange(playjectorState);
    });

    // Subscribe to campaign launch results (pavilionLaunchReply)
    this.playjectorService.onCampaignLaunchResult((result) => {
      this.handleCampaignLaunchResult(result);
    });

    // Listen to PavilionCoordinator events (proper cross-coordinator communication!)
    if (pavilionCoordinator) {

      // When campaign config loaded → launch game with timeout tracking
      // NOTE: We listen to campaignConfigLoaded (not campaignAssigned) to ensure
      // the campaign config is fetched before we try to launch
      pavilionCoordinator.events.campaignConfigLoaded.addListener(() => {
        console.log('[GameLaunchCoordinator] Campaign config loaded, launching game');

        Sentry.logger.info('[GameLaunchCoordinator] Campaign config loaded, launching game'); // This is temp!
        
        this.launchCampaignWithTracking(pavilionCoordinator);
      });

      // NOTE: Game should be running BEFORE match found.
      // When match found, PavilionCoordinator orchestrates stream start via SessionCoordinator.
      // GameLaunchCoordinator does not need to do anything on match found.
    }

    // Initialize managers
    this.managers.lifecycle.init(sessionCoordinator);
    this.managers.campaignSwitch.init();

    // Send initial Pavilion edition to Playjector
    // This ensures Playjector knows we're in Pavilion mode from the start
    if (pavilionCoordinator) {
      console.log('[GameLaunchCoordinator] Sending initial Pavilion edition to Playjector');
      const isGuestAccount = window.playcast?.guestAccount ?? false;
      this.playjectorService.sendHostModeChanged(
        'pavilion',
        false, // matchmakeable (will be set when game launches)
        false, // steamGuestLogin (will be set when credentials available)
        isGuestAccount, // Read actual guest account status from window.playcast
        '',    // credentials (empty for now)
        pavilionCoordinator.getAuthId() || '',
        undefined // campaignId (not assigned yet)
      );
    }

    // Listen to gameLaunched event. Whether we run the legacy on-launch macro
    // or hand off to StatefulMacroCoordinator depends on the campaign config.
    // The fork lives here so the per-launch flow remains a single observable
    // chain (`gameLaunched -> [legacy | stateful walk] -> gameReady`).
    this.events.gameLaunched.addListener(async (data) => {
      if (!this.isLaunchEventAdmissible()) return;
      // Each fresh launch resets the per-launch idempotency flag for the
      // gameReady emit. The two completion paths (legacy + stateful) both
      // funnel through `signalLaunchMacroWalkComplete()` so this is the
      // single source of truth for "we have not yet emitted gameReady for
      // *this* launch".
      this.lastLaunchData = data;
      this.launchMacroWalkComplete = false;
      this.launchMacroGeneration += 1;

      if (this.shouldUseStatefulMacroSystem()) {
        console.log(
          '[GameLaunchCoordinator] Game launched, useStatefulMacroSystem=true; ' +
          'StatefulMacroCoordinator will drive the walk and call signalLaunchMacroWalkComplete()',
        );
        return;
      }

      console.log('[GameLaunchCoordinator] Game launched, running legacy onLaunchMacro path');
      await this.executeLegacyOnLaunchMacro();
    });

    // Listen to gameClosed event to reset macro tracking
    this.events.gameClosed.addListener(() => {
      if (this.managers.macro) {
        console.log('[GameLaunchCoordinator] Game closed, resetting macro tracking');
        this.managers.macro.reset();
      }
    });

    this.state.updateState({ initialized: true });
    console.log('[GameLaunchCoordinator] Initialized');
  }

  /**
   * Register IPC replication handler with the replication router
   *
   * Called during coordinator initialization to set up message routing.
   *
   * Pattern (follows MessageRouter):
   * - Coordinator registers handler for specific IPC channel
   * - When IPC replication arrives, router calls our handler
   * - Handler updates store, manager emits events
   */
  public registerReplicationHandler(router: IReplicationRouter): void {
    router.register('playjectorHostModeState', this.onReplicationReceived.bind(this));
    console.log('[GameLaunchCoordinator] Registered replication handler');
  }

  /**
   * Handle Playjector state changes from service
   * @private
   *
   * Simply updates the store - manager will detect changes and emit events
   */
  private handlePlayjectorStateChange(playjectorState: PlayjectorState): void {
    // Update store with new state
    // Manager listens to store changes and emits events
    this.gameModeStore.updateState({
      gameLaunched: playjectorState.gameLaunched,
      gameName: playjectorState.gameName,
      gameId: playjectorState.gameId,
      steamLaunched: playjectorState.steamLaunched,
      steamWindow: playjectorState.steamWindow,
      userLoggedIn: playjectorState.userLoggedIn,
    });
  }

  /**
   * Get initialization status
   */
  public isInitialized(): boolean {
    return this.state.currentState.initialized;
  }

  /**
   * Stop the running game by terminating the game process (not Playjector itself).
   * Used when the API clears the campaign and we need to terminate the game process.
   */
  public stopGame(hostAuthId: string): void {
    if (!this.gameModeStore.currentState.gameLaunched) {
      console.log('[GameLaunchCoordinator] No game running, nothing to stop');
      return;
    }

    console.log('[GameLaunchCoordinator] Stopping game (terminating pavilion process)');
    this.managers.campaignSwitch.cancelPendingSwitch('campaign_cleared');
    this.playjectorService.terminateGame(hostAuthId);
  }

  /**
   * Get current game state
   */
  public getGameState(): { gameLaunched: boolean; gameId: number; gameName: string } {
    const { gameLaunched, gameId, gameName } = this.gameModeStore.currentState;
    return { gameLaunched, gameId, gameName };
  }

  /**
   * Check if there's a pending campaign switch (for testing)
   */
  public hasPendingCampaignSwitch(): boolean {
    return this.managers.campaignSwitch.hasPendingSwitch();
  }

  /**
   * Get the campaign ID of the pending switch (for testing)
   */
  public getPendingCampaignId(): string | null {
    return this.managers.campaignSwitch.getPendingCampaignId();
  }

  /** Internal lifecycle events must belong to the current launch attempt. */
  public isCurrentCampaignLaunchGeneration(generation: number): boolean {
    return generation === this.campaignLaunchGeneration;
  }

  /**
   * Report campaign launch result directly (for testing)
   *
   * In production, this is called by Playjector via IPC messages.
   * For tests, this allows direct result reporting.
   */
  public reportCampaignLaunchResult(result: 'SUCCESS' | 'FAILURE', reason?: string): void {
    this.managers.campaignSwitch.reportResult(result, reason);
  }

  /**
   * Register a settle guard on the internal CampaignSwitchManager so long
   * postMacroDelayMs waits don't trip the campaign-switch timeout.
   */
  public setCampaignSwitchSettleGuard(guard: () => boolean): void {
    this.managers.campaignSwitch.setSettleGuard(guard);
  }

  /**
   * Execute onJoinMacro (called by PavilionCoordinator before starting stream)
   *
   * This triggers campaign-specific actions like unpausing the game.
   * Awaited before HOST_READY is sent and the player can connect.
   */
  public async executeOnJoinMacro(): Promise<void> {
    if (this.managers.macro) {
      await this.managers.macro.executeOnJoinMacro();
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager not initialized, cannot execute onJoinMacro');
    }
  }

  /**
   * Execute onExitMacro (called by PavilionCoordinator when all players leave, no restart)
   *
   * Awaited before re-entering the matchmaking pool. After the macro completes,
   * the inactive loop is started automatically.
   */
  public async executeOnExitMacro(): Promise<void> {
    if (this.managers.macro) {
      await this.managers.macro.executeOnExitMacro();
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager not initialized, cannot execute onExitMacro');
    }
  }

  /**
   * Signal that the launch macro walk has completed.
   *
   * Called by:
   *  - `executeLegacyOnLaunchMacro()` after the legacy `onLaunchMacro` finishes
   *    (preserves existing behavior — gameReady fires once at the end).
   *  - `StatefulMacroCoordinator` when the macro tree walk reaches a leaf node
   *    (`status: 'step', isLeaf: true`).
   *
   * Idempotent within a single launch. The second and subsequent calls are
   * silent no-ops; the gateway is reset on the next `gameLaunched` event so a
   * subsequent launch can emit `gameReady` again. This guards against:
   *  - The stateful coordinator + a late legacy fallback both signalling.
   *  - Multiple `MacroWalkResponse { isLeaf: true }` arriving (e.g. duplicated
   *    on WS reconnect) for the same launch.
   */
  public signalLaunchMacroWalkComplete(): void {
    if (this.launchMacroWalkComplete) {
      console.log('[GameLaunchCoordinator] signalLaunchMacroWalkComplete: already signalled this launch — no-op');
      return;
    }

    this.launchMacroWalkComplete = true;

    if (!this.lastLaunchData) {
      console.warn('[GameLaunchCoordinator] signalLaunchMacroWalkComplete called before any gameLaunched event — skipping gameReady emit');
      return;
    }

    console.log('[GameLaunchCoordinator] ✅ Launch macro walk complete, emitting gameReady');
    this.events.gameReady.emit(this.lastLaunchData);
  }

  /**
   * Run the legacy `onLaunchMacro` and emit `gameReady` afterwards.
   *
   * Code-move-only refactor of the prior inline behavior in the `gameLaunched`
   * listener. The pre-refactor sequence was:
   *
   *   gameLaunched -> await macroManager.executeOnLaunchMacro() -> emit gameReady
   *
   * This method preserves that exact sequence. It is invoked by:
   *  - The `gameLaunched` listener when `useStatefulMacroSystem !== true`
   *    (default path).
   *  - `StatefulMacroCoordinator` when the API responds with `status: 'disabled'`
   *    or `status: 'error'` and we need to fall back to the legacy macro.
   *
   * Calls `signalLaunchMacroWalkComplete()` at the end, which itself is
   * idempotent — so multiple invocations during a single launch (e.g. legacy
   * listener + stateful disabled fallback) collapse to a single gameReady emit.
   */
  public async executeLegacyOnLaunchMacro(): Promise<void> {
    const macroGeneration = this.launchMacroGeneration;
    if (this.managers.macro) {
      await this.managers.macro.executeOnLaunchMacro();
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager not initialized, skipping legacy onLaunchMacro');
    }
    if (macroGeneration === this.launchMacroGeneration) this.signalLaunchMacroWalkComplete();
  }

  /**
   * Returns true iff the currently-loaded campaign config opts into the
   * stateful macro tree walk (Phase 1 schema flag).
   *
   * False if no campaign is loaded yet — defensive default; the legacy path
   * is the safe fallback when we can't tell.
   */
  private shouldUseStatefulMacroSystem(): boolean {
    const cfg = this.pavilionCoordinator?.getCampaignConfig();
    return cfg?.useStatefulMacroSystem === true;
  }

  /**
   * Handle replication message from Electron (via IPC)
   *
   * This is called by ReplicationRouter when Electron sends playjectorHostModeState updates.
   * Playjector runs in the Electron main process and replicates its state to the renderer.
   *
   * NOTE: This is the ONLY way GameLaunchCoordinator receives Playjector state in production!
   * MockPlayjectorService simulates this by calling onStateChange() with similar data.
   *
   * @param unknownState - playjectorHostModeState from Electron (needs type narrowing)
   */
  public onReplicationReceived(unknownState: unknown): void {
    console.log('[GameLaunchCoordinator] Received replication from Electron:', unknownState);

    // Type narrowing: cast to PlayjectorHostEdition
    const state = unknownState as PlayjectorHostEdition;

    // Extract game launch state
    const { gameLaunched, gameId, gameName, steamLaunched, steamWindow, userLoggedIn } = state;

    // Update game mode store - manager listens to changes and emits events
    // This is the same pattern as handlePlayjectorStateChange()
    this.gameModeStore.updateState({
      gameLaunched,
      gameId,
      gameName,
      steamLaunched,
      steamWindow,
      userLoggedIn,
    });

    // NOTE: Lifecycle manager automatically detects store changes and emits events.
    // No need to call manager methods directly.
  }

  // ========================================
  // Private - API Integration
  // ========================================

  // NOTE: GameLaunchCoordinator does NOT send API messages directly!
  // It emits events that PavilionCoordinator listens to.
  // PavilionCoordinator owns matchmaking state and handles API communication.

  // ========================================
  // Private - Cross-Coordinator Integration
  // ========================================

  /**
   * Notify Playjector of Pavilion state changes
   * This is the PROPER architecture: GameLaunchCoordinator listens to PavilionCoordinator events
   * and decides when to call the service
   */
  private notifyPlayjectorOfPavilionState(pavilionCoordinator: PavilionCoordinator): void {
    if (!this.playjectorService) {
      console.warn('[GameLaunchCoordinator] Service not initialized');
      return;
    }

    if (!pavilionCoordinator) {
      console.warn('[GameLaunchCoordinator] PavilionCoordinator not provided');
      return;
    }

    // Read state from PavilionCoordinator (via public API)
    const invitation = pavilionCoordinator.getInvitation();
    const credentials = pavilionCoordinator.getCredentials();
    const campaign = pavilionCoordinator.getCampaignDetails();
    const hostState = pavilionCoordinator.getHostMatchmakingState();
    const authId = pavilionCoordinator.getAuthId();

    // Determine Steam guest login setting from invitation
    const useSteamGuestLogin =
      invitation?.type === 'cafe' &&
      invitation.manifest?.useSteamGuestLogin === true;

    // Format credentials
    const credentialsToSend = credentials.username && credentials.password
      ? `${credentials.username} ${credentials.password}`
      : '';

    console.log('[GameLaunchCoordinator] Notifying Playjector of Pavilion state:', {
      edition: 'pavilion',
      matchmakeable: hostState.matchmakeable,
      useSteamGuestLogin,
      guestAccount: hostState.guestAccount,
      hasCredentials: !!credentialsToSend,
      authId,
      campaignId: campaign.campaignId,
    });

    // Call service (this coordinator owns the service!)
    this.playjectorService.sendHostModeChanged(
      'pavilion',
      hostState.matchmakeable,
      useSteamGuestLogin,
      hostState.guestAccount,
      credentialsToSend,
      authId,
      campaign.campaignId,
    );
  }

  /** Shared admission for legacy and stateful consumers; preparation stays closed until current dispatch. */
  public isLaunchEventAdmissible(): boolean {
    return this.admittedLaunchGeneration === this.campaignLaunchGeneration;
  }

  /** Launch a campaign with timeout tracking. */
  private async launchCampaignWithTracking(pavilionCoordinator: PavilionCoordinator): Promise<void> {
    const launchGeneration = ++this.campaignLaunchGeneration;
    const campaign = pavilionCoordinator.getCampaignDetails();
    const campaignId = campaign.campaignId;
    const campaignConfig = campaign.config;

    if (!campaignId) {
      console.warn('[GameLaunchCoordinator] Cannot launch campaign: no campaign ID');
      Sentry.logger.warn('[GameLaunchCoordinator] Cannot launch campaign: no campaign ID')
      return;
    }

    if (!campaignConfig) {
      console.error('[GameLaunchCoordinator] Cannot launch campaign: no campaign config');
      Sentry.logger.error('[GameLaunchCoordinator] Cannot launch campaign: no campaign config')
      return;
    }

    // Invalidate both macro consumers before configuration lookup or restore can stop a process.
    this.launchMacroGeneration += 1;
    this.launchMacroWalkComplete = true;
    this.lastLaunchData = null;
    this.events.launchWorkInvalidated.emit({ reason: 'campaign-launch', nextOwner: 'campaign-launch' });
    const preparation = await this.prepareGameSaveBeforeCampaignLaunch(
      pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
    if (preparation.status === 'failed') {
      this.reportCampaignSwitchFailure(campaignId, preparation.reason);
      return;
    }
    if (preparation.status === 'superseded') {
      this.failIfCampaignConfigChangedDuringRestore(pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
      return;
    }
    const configured = preparation.configured;

    console.log('[GameLaunchCoordinator] Starting campaign launch with tracking:', campaignId);
    Sentry.logger.warn(`[GameLaunchCoordinator] Starting campaign launch with tracking: ${campaignId}`); // This is temp!
    
    this.analytics.sendGameLaunchAttemptedEvent({
      campaignId,
      gameName: campaignConfig.displayName,
    });

    // DEBUG: Log the full campaign config to see what we received from API
    console.log('[GameLaunchCoordinator] 📋 Full campaign config:', {
      campaignId: campaignConfig.campaignId,
      displayName: campaignConfig.displayName,
      edition: campaignConfig.edition,
      active: campaignConfig.active,
      launchPackage: campaignConfig.launchPackage,
    });

    // Get game-specific ready wait time (CampaignSwitchManager will add the base timeout)
    const readyWaitTime = campaignConfig.launchPackage?.readyWaitTimeMs || 0;

    // Start tracking with timeout (pass only readyWaitTime, manager adds base timeout)
    const switchPromise = this.managers.campaignSwitch.startSwitch(campaignId, readyWaitTime);

    // Update Playjector with host mode/credentials (must come before launch)
    this.notifyPlayjectorOfPavilionState(pavilionCoordinator);

    // Get auth ID for launch command
    const authId = pavilionCoordinator.getAuthId();

    // CRITICAL: Actually launch the game via processLaunch IPC message
    console.log('[GameLaunchCoordinator] 🎮 Sending processLaunch IPC command:', {
      campaignId,
      displayName: campaignConfig.displayName,
      launcher: campaignConfig.launchPackage?.launcher,
      authId,
    });

    Sentry.logger.warn('[GameLaunchCoordinator] 🎮 Sending processLaunch IPC command:', { // This is temp!
      campaignId,
      displayName: campaignConfig.displayName,
      launcher: campaignConfig.launchPackage?.launcher,
    });

    // Scope subsequent gameLaunched/gameReady events to this restored launch.
    // The mock can deliver ready before the launch reply; Pavilion buffers it
    // until a SUCCESS reply for this generation. Older events stay gated.
    if (configured) this.events.gameSaveLaunchDispatched.emit({ campaignId, generation: launchGeneration });
    if (!this.isCurrentCampaignLaunch(pavilionCoordinator, campaignId, campaignConfig, launchGeneration)) {
      if (launchGeneration === this.campaignLaunchGeneration) {
        this.managers.campaignSwitch.cancelPendingSwitch('game_save_launch_superseded');
      }
      this.failIfCampaignConfigChangedDuringRestore(pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
      return;
    }
    // Configuration/restored/dispatched event callbacks still see a closed gate.
    // Only the current generation's actual dispatch admits either macro consumer.
    this.admittedLaunchGeneration = launchGeneration;
    this.playjectorService.launchCampaign(
      authId,
      'pavilion',
      campaignConfig.launchPackage,
    );

    // Wait for result (this will resolve when result is received or timeout fires)
    const result = await switchPromise;
    // Ordinary disconnect refreshes can replace an unmanaged campaign's config
    // after game-ready but before this reply. Restored saves still pin that config.
    if (!this.isCurrentCampaignLaunch(pavilionCoordinator, campaignId, campaignConfig, launchGeneration, configured)) {
      this.failIfCampaignConfigChangedDuringRestore(pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
      console.log('[GameLaunchCoordinator] Ignoring launch result for a superseded campaign');
      return;
    }

    if (result.success) {
      console.log('[GameLaunchCoordinator] ✅ Campaign launch successful:', campaignId);
      if (configured) this.events.gameSaveLaunchConfirmed.emit({ campaignId, generation: launchGeneration });
      this.reportCampaignSwitchSuccess(campaignId);
    } else {
      console.error('[GameLaunchCoordinator] ❌ Campaign launch failed:', result.reason);
      Sentry.logger.error(`[GameLaunchCoordinator] ❌ Campaign launch failed: ${result.reason}`);
      this.reportCampaignSwitchFailure(campaignId, result.reason, result.transient);
    }
  }

  /** Select/check/restore phases finish before a campaign launch tracker is created. */
  private async prepareGameSaveBeforeCampaignLaunch(
    pavilion: PavilionCoordinator, campaignId: string, configuration: HostCampaignConfig, generation: number,
  ): Promise<CampaignSavePreparation> {
    const current = () => this.isCurrentCampaignLaunch(pavilion, campaignId, configuration, generation);
    try { this.campaignGameSaveConfiguration?.updateCampaignConfiguration(configuration); }
    catch { return { status: 'failed', reason: 'Dashboard game save settings are invalid' }; }
    this.events.gameSaveCampaignConfigurationSelected.emit({ campaignId, generation, configuration: configuration.gameSave });
    if (!current()) return { status: 'superseded' };
    try {
      const campaign = this.gameSaveCampaignFromConfig(configuration);
      const configured = await this.hasConfiguredGameSave(campaign, configuration);
      if (!current()) return { status: 'superseded' };
      this.events.gameSaveConfigurationChecked.emit({ campaignId, configured });
      if (!current()) return { status: 'superseded' };
      if (configured) {
        if (!campaign) throw new Error('Configured game save has no supported game identity');
        await this.restoreGameSaveBeforeCampaignLaunch(pavilion, campaign, configuration, generation);
        if (!current()) return { status: 'superseded' };
      }
      return { status: 'ready', configured };
    } catch (error) {
      return current() ? { status: 'failed', reason: error instanceof Error ? error.message : String(error) }
        : { status: 'superseded' };
    }
  }

  private async restoreGameSaveBeforeCampaignLaunch(
    pavilion: PavilionCoordinator, campaign: GameSaveCampaign, configuration: HostCampaignConfig, generation: number,
  ): Promise<void> {
    const current = () => this.isCurrentCampaignLaunch(pavilion, campaign.campaignId, configuration, generation);
    this.events.gameSaveRestoreStarted.emit({ campaignId: campaign.campaignId });
    if (!current()) return;
    const requireStopped = configuration.gameSave?.enabled === true ? configuration.gameSave.requireStopped : true;
    // Steam's tracked process must release its save; native loadGame stops the selected Android VM.
    if (configuration.launchPackage.launcher === 'steam' && requireStopped) {
      await this.managers.processStop.stopBeforeRestore(pavilion.getAuthId());
    }
    if (!current()) return;
    await this.restoreConfiguredGameSave(campaign, requireStopped);
    if (!current()) return;
    this.events.gameSaveRestored.emit({ campaignId: campaign.campaignId });
  }

  private gameSaveCampaignFromConfig(campaignConfig: HostCampaignConfig): GameSaveCampaign | null {
    const launchPackage = campaignConfig.launchPackage;
    if (launchPackage.launcher === 'steam') {
      return {
        campaignId: campaignConfig.campaignId,
        launchPackage: { launcher: 'steam', launchId: launchPackage.launchId },
      };
    }
    if (launchPackage.launcher === 'android') {
      return {
        campaignId: campaignConfig.campaignId,
        launchPackage: { launcher: 'android', package: launchPackage.package },
      };
    }
    return null;
  }

  private async hasConfiguredGameSave(campaign: GameSaveCampaign | null, configuration: HostCampaignConfig): Promise<boolean> {
    if (!campaign) return false;
    const key = JSON.stringify(campaign);
    if (configuration.gameSave?.enabled === false) {
      this.confirmedConfiguredSaveKeys.delete(key);
      return false;
    }
    let lookup: GameSaveConfigurationAvailabilityResult;
    try {
      lookup = (await this.gameSaveConfigurationAvailability?.hasGameSaveConfiguration(campaign)) ?? {
        status: 'unavailable' as const,
        error: { code: 'internal' as const, retryable: true, message: 'Native game save configuration service is unavailable' },
      };
    } catch (error) {
      lookup = {
        status: 'unavailable' as const,
        error: { code: 'internal' as const, retryable: true, message: error instanceof Error ? error.message : String(error) },
      };
    }

    if (lookup.status === 'configured') {
      this.confirmedConfiguredSaveKeys.add(key);
      return true;
    }
    if (configuration.gameSave?.enabled === true) {
      const detail = lookup.status === 'unavailable'
        ? `${lookup.error.code}: ${lookup.error.message}` : 'Native process reported no game save configuration';
      throw new Error(`Dashboard-configured game save cannot be verified; refusing to launch without reset (${detail})`);
    }
    if (this.confirmedConfiguredSaveKeys.has(key)) {
      throw new Error('Previously configured game save cannot be verified; refusing to launch without reset');
    }
    if (lookup.status === 'unavailable') {
      // Preserve legacy launches for games that have never been confirmed to
      // use managed saves. A known configured game remains fail-closed above.
      console.warn('[GameLaunchCoordinator] Save configuration unavailable; launching unconfirmed game normally:', lookup.error.message);
    }
    return false;
  }

  private async restoreConfiguredGameSave(campaign: GameSaveCampaign, requireStopped: boolean): Promise<void> {
    if (!this.gameSaveManager) throw new Error('Game save manager is unavailable');
    const result = await this.gameSaveManager.loadGame({ campaign, requireStopped });
    if (result.status === 'failed') throw new Error(`Game save restore failed: ${result.error.message}`);
  }

  private isCurrentCampaignLaunch(
    pavilionCoordinator: PavilionCoordinator,
    campaignId: string,
    campaignConfig: HostCampaignConfig,
    launchGeneration: number,
    requirePinnedConfiguration = true,
  ): boolean {
    const current = pavilionCoordinator.getCampaignDetails();
    return launchGeneration === this.campaignLaunchGeneration &&
      current.campaignId === campaignId &&
      (!requirePinnedConfiguration || current.config === campaignConfig);
  }

  private failIfCampaignConfigChangedDuringRestore(
    pavilionCoordinator: PavilionCoordinator,
    campaignId: string,
    campaignConfig: HostCampaignConfig,
    launchGeneration: number,
  ): void {
    const current = pavilionCoordinator.getCampaignDetails();
    if (launchGeneration === this.campaignLaunchGeneration &&
      current.campaignId === campaignId && current.config !== campaignConfig) {
      this.reportCampaignSwitchFailure(campaignId, 'Campaign config changed during game save restore');
    }
  }

  /**
   * Handle campaign launch result from Playjector service (pavilionLaunchReply)
   */
  private handleCampaignLaunchResult(result: CampaignLaunchResult): void {
    console.log('[GameLaunchCoordinator] Received campaign launch result:', result);

    const { result: resultType, message } = result;

    // Report result to tracker (this will resolve the pending switch promise)
    this.managers.campaignSwitch.reportResult(resultType, message);

    console.log('[GameLaunchCoordinator] ✅ Reported result to CampaignSwitchManager');
  }

  /**
   * Report successful campaign switch
   *
   * Emits event for PavilionCoordinator to update matchmaking state and notify API
   */
  private reportCampaignSwitchSuccess(campaignId: string): void {
    console.log('[GameLaunchCoordinator] ✅ Campaign switch succeeded:', campaignId);

    // Emit event - PavilionCoordinator will listen and update its own state + notify API
    this.events.campaignSwitchSucceeded.emit({ campaignId });
  }

  /**
   * Report failed campaign switch.
   *
   * Public so the StatefulMacroCoordinator can escalate `macro_walk_max_retries`
   * through the same downstream pipeline as a regular launch failure
   * (PavilionCoordinator listens for `campaignSwitchFailed` and emits
   * `MATCHMAKER_CAMPAIGN_SWITCH_FAILURE` to the API). Internal callers continue
   * to use this for the existing tracker-result-based failure path.
   *
   * Emits event for PavilionCoordinator to update matchmaking state and notify API.
   */
  public reportCampaignSwitchFailure(campaignId: string, reason: string, transient?: boolean): void {
    console.error('[GameLaunchCoordinator] ❌ Campaign switch failed:', campaignId, reason);

    if (this.managers.campaignSwitch.getPendingCampaignId() === campaignId) {
      // External deadlines must settle the tracker before downstream cleanup.
      // Invalidate its continuation so cancellation cannot produce a second failure or late success.
      this.campaignLaunchGeneration += 1;
      this.managers.campaignSwitch.cancelPendingSwitch(reason);
    }

    // Emit event - PavilionCoordinator will listen and update its own state + notify API
    this.events.campaignSwitchFailed.emit({ campaignId, reason, transient: transient ?? false });
  }

  /**
   * Cleanup coordinator (for testing/unmounting)
   *
   * CRITICAL: Cleans up all timers and pending operations
   */
  public destroy(): void {
    console.log('[GameLaunchCoordinator] Destroying coordinator...');

    // Cancel any pending campaign switches (clears timeout)
    this.managers.campaignSwitch.cancelPendingSwitch('coordinator destroyed');

    // Unsubscribe from service
    if (this.unsubscribeFromService) {
      this.unsubscribeFromService();
      this.unsubscribeFromService = undefined;
    }

    console.log('[GameLaunchCoordinator] Destroyed');
  }
}

// ========================================
// NOTE: No singleton export here!
// ========================================
//
// GameLaunchCoordinator instances are created and managed by init.ts
// This avoids loading GlobalConfig at module load time (which breaks tests).
//
// Import from init.ts instead:
// import { playjectorCoordinator } from './coordinators/init';
